import React from "react";
import { CheckCircle2, EyeOff, ReceiptText } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { fmtDate } from "../lib/api";

/**
 * Phase 169 — shared UI bits for portals whose receipts may be hidden by the municipality.
 *
 *   <ReceiptHiddenBanner rv={rv} />            inline notice above a receipts list (only renders when something is hidden)
 *   <ReceiptHiddenBadge />                     small "Receipt hidden" chip used in place of a receipt link
 *   <ReceiptHiddenCard ... />                  full-page state for /receipt/:no and the public receipt link
 */
export const ReceiptHiddenBanner = ({ rv, className = "", testId = "receipt-hidden-banner" }) => {
  if (!rv || !rv.governed) return null;
  if (rv.visibleNow && !rv.paymentGated) return null;
  const s = rv.state || {};
  return (
    <Alert className={`border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/5 ${className}`} data-testid={testId}>
      <EyeOff className="h-4 w-4 text-[hsl(var(--warning))]" aria-hidden="true" />
      <AlertTitle className="text-sm">{rv.visibleNow ? "Some receipts cannot be opened" : "Receipts cannot be opened right now"}</AlertTitle>
      <AlertDescription className="text-xs text-muted-foreground">
        {!rv.visibleNow && (rv.message || "Receipt viewing is currently turned off by the municipality for your account type.")}
        {!rv.visibleNow && s.reason === "outside_window" && s.window_from && s.today < s.window_from && <> Receipts open again from {fmtDate(s.window_from)}.</>}
        {rv.visibleNow && rv.paymentGated && (
          <>Only receipts for payments dated {s.payment_from ? fmtDate(s.payment_from) : "any date"} to {s.payment_to ? fmtDate(s.payment_to) : "onwards"} can be opened. Payments themselves are always recorded.</>
        )}
      </AlertDescription>
    </Alert>
  );
};

export const ReceiptHiddenBadge = ({ testId = "receipt-hidden-badge", label = "Receipt hidden" }) => (
  <Badge variant="outline" className="text-[10px] gap-1 text-muted-foreground border-dashed" data-testid={testId} title="Receipt viewing is turned off by the municipality for your account type">
    <EyeOff className="h-3 w-3" aria-hidden="true" /> {label}
  </Badge>
);

export const ReceiptHiddenCard = ({ receiptNumber, amount, paymentDate, message, children, testId = "receipt-hidden" }) => (
  <div className="bg-card border rounded-xl shadow-sm p-6 sm:p-8 text-center space-y-3" data-testid={testId}>
    <div className="h-14 w-14 mx-auto rounded-full bg-muted flex items-center justify-center">
      <ReceiptText className="h-7 w-7 text-muted-foreground" aria-hidden="true" />
    </div>
    <p className="text-lg font-semibold font-heading">Payment recorded — receipt not available for viewing</p>
    <p className="text-sm text-muted-foreground max-w-md mx-auto" data-testid={`${testId}-message`}>
      {message || "Receipt viewing is currently turned off by the municipality for your account type. Your payment is recorded and the receipt is kept safely on file."}
    </p>
    {(receiptNumber || amount != null) && (
      <div className="inline-flex flex-wrap items-center justify-center gap-x-6 gap-y-1 rounded-lg border bg-muted/40 px-4 py-2 text-sm">
        {receiptNumber && <span>Receipt No. <span className="font-mono font-semibold" data-testid={`${testId}-receipt-number`}>{receiptNumber}</span></span>}
        {amount != null && <span>Amount <span className="font-semibold tabular-nums" data-testid={`${testId}-amount`}>{amount}</span></span>}
        {paymentDate && <span>Paid on <span className="font-medium">{fmtDate(paymentDate)}</span></span>}
      </div>
    )}
    {children}
  </div>
);

/**
 * Post-payment confirmation shown INSTEAD of navigating to the receipt page when the municipality has hidden
 * receipts for this role: "Payment successful" + amount + receipt number, no view / print / download.
 */
export const PaymentRecordedDialog = ({ open, onClose, receiptNumber, amount, outstandingAfter, isPartial, message, testId = "payment-recorded-dialog" }) => (
  <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
    <DialogContent className="sm:max-w-md" data-testid={testId}>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-[hsl(var(--success))]" aria-hidden="true" /> {isPartial ? "Partial payment recorded" : "Payment successful"}</DialogTitle>
        <DialogDescription>The payment has been recorded with the municipality.</DialogDescription>
      </DialogHeader>
      <div className="rounded-lg border bg-muted/40 p-4 text-center space-y-1">
        {amount != null && <p className="text-3xl font-semibold tabular-nums" data-testid={`${testId}-amount`}>{amount}</p>}
        {receiptNumber && <p className="text-sm">Receipt No. <span className="font-mono font-semibold" data-testid={`${testId}-receipt-number`}>{receiptNumber}</span></p>}
        {outstandingAfter != null && outstandingAfter > 0 && <p className="text-xs text-muted-foreground">Balance outstanding after this payment: <span className="font-medium text-foreground">{typeof outstandingAfter === "number" ? `Rs.${outstandingAfter.toLocaleString("en-IN")}` : outstandingAfter}</span></p>}
      </div>
      <Alert className="border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/5" data-testid={`${testId}-hidden-notice`}>
        <EyeOff className="h-4 w-4 text-[hsl(var(--warning))]" aria-hidden="true" />
        <AlertTitle className="text-sm">Receipt not available for viewing</AlertTitle>
        <AlertDescription className="text-xs text-muted-foreground">{message || "Receipt viewing is currently turned off by the municipality for your account type. The receipt is kept safely on file."}</AlertDescription>
      </Alert>
      <DialogFooter>
        <Button onClick={onClose} data-testid={`${testId}-close`}>Done</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
